import base from "../middlewares/interceptors";
import {
  getResearchIssuesDetailPaginatedURl,
  getRuleResearchIssuesHistoryURl,
  getGenericResearchIssuesDetailPaginatedURl,
  massUpdateRuleResearchIssuesURl,
  updateRuleResearchIssueURl,
  // getGenericResearchIssuesHistoryURl,
  getGenericResearchIssuesDetailWithoutIssuesPaginatedURl,
  getResearchIssuesDetailWithoutIssuesPaginatedURl,
} from "./constants";

export interface IResearchIssuesParams {
  execution_id: number;
  page: number;
  size: number;
  query_name?: string;
  [key: string]: any; // For additional filter parameters
}

export interface IUpdateRuleResearchIssuePayload {
  assigned_user?: string;
  assigned_by?: string;
  comment?: string;
  status?: string;
}

export interface IMassUpdateRuleResearchIssuesPayload {
  issue_ids: number[];
  issues_update: IUpdateRuleResearchIssuePayload;
}

/**
 * Fetch paginated research issues data
 * @param params - Query parameters including execution_id, page, size, query_name, and filters
 * @returns Promise with research issues data
 */
export const getResearchIssuesDetailPaginated = async (
  params: IResearchIssuesParams
) => {
  try {
    const queryParams = new URLSearchParams();

    // Add mandatory parameters
    queryParams.append("execution_id", params.execution_id.toString());
    queryParams.append("page", params.page.toString());
    queryParams.append("size", params.size.toString());

    // Add optional parameters
    if (params.query_name) {
      queryParams.append("query_name", params.query_name);
    }

    // Add any additional filter parameters
    Object.entries(params).forEach(([key, value]) => {
      if (
        key !== "execution_id" &&
        key !== "page" &&
        key !== "size" &&
        key !== "query_name" &&
        value
      ) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await base.get(
      `${getResearchIssuesDetailPaginatedURl}?${queryParams.toString()}`
    );
    return response?.data;
  } catch (error) {
    console.error("Error fetching research issues:", error);
    throw error;
  }
};

/**
 * Fetch paginated research  without issues data
 * @param params - Query parameters including execution_id, page, size, query_name, and filters
 * @returns Promise with research issues data
 */

export const getResearchIssuesDetailPaginatedWithoutIssues = async (
  params: IResearchIssuesParams
) => {
  try {
    const queryParams = new URLSearchParams();

    // Add mandatory parameters
    //queryParams.append("execution_id", params.execution_id.toString());
    queryParams.append("page", params.page.toString());
    queryParams.append("size", params.size.toString());

    // Add optional parameters
    if (params.query_name) {
      queryParams.append("name", params?.query_name);
    }

    // Add any additional filter parameters
    Object.entries(params).forEach(([key, value]) => {
      if (
        key !== "execution_id" &&
        key !== "page" &&
        key !== "size" &&
        key !== "query_name" &&
        value
      ) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await base.get(
      `${getResearchIssuesDetailWithoutIssuesPaginatedURl}/${
        params.execution_id
      }?${queryParams.toString()}`
    );
    return response?.data;
  } catch (error) {
    console.error("Error fetching research issues:", error);
    throw error;
  }
};

/**
 * Fetch paginated generic research issues data
 * @param params - Query parameters including execution_id, page, size, query_name, and filters
 * @returns Promise with research issues data
 */
export const getGenericResearchIssuesDetailPaginated = async (
  params: IResearchIssuesParams
) => {
  try {
    const queryParams = new URLSearchParams();

    // Add mandatory parameters
    queryParams.append(
      "generic_research_query_execution_id",
      params.execution_id.toString()
    );
    queryParams.append("page", params.page.toString());
    queryParams.append("size", params.size.toString());

    // Add optional parameters
    if (params.query_name) {
      queryParams.append("query_name", params?.query_name);
    }

    // Add any additional filter parameters
    Object.entries(params).forEach(([key, value]) => {
      if (
        key !== "execution_id" &&
        key !== "page" &&
        key !== "size" &&
        key !== "query_name" &&
        value
      ) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await base.get(
      `${getGenericResearchIssuesDetailPaginatedURl}?${queryParams.toString()}`
    );
    return response?.data;
  } catch (error) {
    console.error("Error fetching research issues:", error);
    throw error;
  }
};

/**
 * Fetch paginated generic research issues data
 * @param params - Query parameters including execution_id, page, size, query_name, and filters
 * @returns Promise with research issues data
 */
export const getGenericResearchIssuesDetailPaginatedWithoutIssues = async (
  params: IResearchIssuesParams
) => {
  try {
    const queryParams = new URLSearchParams();

    // Add mandatory parameters
    //queryParams.append("execution_id", params.execution_id.toString());
    queryParams.append("page", params.page.toString());
    queryParams.append("size", params.size.toString());

    // Add optional parameters
    if (params.query_name) {
      queryParams.append("name", params?.query_name);
    }

    // Add any additional filter parameters
    Object.entries(params).forEach(([key, value]) => {
      if (
        key !== "execution_id" &&
        key !== "page" &&
        key !== "size" &&
        key !== "query_name" &&
        value
      ) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await base.get(
      `${getGenericResearchIssuesDetailWithoutIssuesPaginatedURl}/${
        params.execution_id
      }/details/pages?${queryParams.toString()}`
    );
    return response?.data;
  } catch (error) {
    console.error("Error fetching research issues:", error);
    throw error;
  }
};

/**
 * Fetch research issue history by issue ID
 * @param issueId - The research issue ID
 * @returns Promise with issue history data
 */
export const getRuleResearchIssueHistory = async (issueId: number) => {
  try {
    const response = await base.get(
      `${getRuleResearchIssuesHistoryURl}/${issueId}`
    );
    return response?.data;
  } catch (error) {
    console.error("Error fetching research issue history:", error);
    throw error;
  }
};
export const getGenericResearchIssueHistory = async (issueId: number) => {
  try {
    const response = await base.get(
      `${getRuleResearchIssuesHistoryURl}/${issueId}`
    );
    return response?.data;
  } catch (error) {
    console.error("Error fetching research issue history:", error);
    throw error;
  }
};

/**
 * Update a single research issue
 * @param issueId - The research issue ID
 * @param payload - Update payload
 * @returns Promise with update response
 */
export const updateRuleResearchIssueNew = async (
  issueId: number,
  payload: IUpdateRuleResearchIssuePayload
) => {
  try {
    const response = await base.post(
      `${updateRuleResearchIssueURl}/${issueId}`,
      payload
    );
    return response?.data;
  } catch (error) {
    console.error("Error updating research issue:", error);
    throw error;
  }
};

/**
 * Mass update multiple research issues
 * @param payload - Mass update payload with issue IDs and update data
 * @returns Promise with mass update response
 */
export const massUpdateRuleResearchIssuesNew = async (
  payload: IMassUpdateRuleResearchIssuesPayload
) => {
  try {
    const response = await base.post(massUpdateRuleResearchIssuesURl, payload);
    return response?.data;
  } catch (error) {
    console.error("Error mass updating research issues:", error);
    throw error;
  }
};

/**
 * Mass update multiple research issues
 * @param payload - Mass update payload with issue IDs and update data
 * @returns Promise with mass update response
 */
